import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect, useMemo, } from "react";
import { TextField, MenuItem, Button, Box, Paper, Typography, Grid, Radio, FormControlLabel, RadioGroup, Select, CircularProgress, Tooltip, IconButton, } from "@mui/material";
import { FaFilePdf, FaPaperclip, FaInfoCircle, FaTimes } from "react-icons/fa";
import { styled } from "@mui/material/styles";
import TableField from "./TableField";
// ============ STYLING ============= //
const StyledPaper = styled(Paper)(({ theme }) => ({
    "& .MuiGrid-container": {
        width: "100%",
        margin: 0,
    },
    width: "100%",
    boxSizing: "border-box",
    boxShadow: "none",
    backgroundColor: "transparent",
    borderRadius: 0,
}));
export const StyledTextField = styled(TextField)(({ theme, isSubmitted, isDisabled }) => ({
    marginBottom: theme.spacing(3),
    width: "100%",
    maxHeight: "300px",
    color: "#fff",
    "& .MuiOutlinedInput-root": {
        borderRadius: "5px",
        height: "50px",
        color: isSubmitted ? "#fff" : "#0E497A",
        backgroundColor: isSubmitted ? "#0E497A" : "transparent",
        "& fieldset": {
            borderColor: isSubmitted ? "#0E497A" : "#D5DDE4",
        },
        "&:hover fieldset": {
            borderColor: isSubmitted ? "#0E497A" : "#D5DDE4",
        },
        "&.Mui-focused fieldset": {
            borderColor: "#0E497A",
        },
        "&.Mui-disabled": {
            backgroundColor: isSubmitted ? "#0E497A" : "rgba(0, 0, 0, 0.12)",
            color: isSubmitted ? "#fff" : "rgba(0, 0, 0, 0.38)",
        },
    },
    "& .MuiSelect-select": {
        height: "50px !important",
        lineHeight: "50px",
        padding: "0 14px",
        color: isSubmitted ? "#fff" : "#0E497A",
    },
    "& .MuiSelect-icon": {
        color: isSubmitted ? "#fff" : "#0E497A",
    },
    "& .MuiInputLabel-root": {
        color: "#0E497A",
        fontFamily: "IBM Plex Sans, sans-serif",
        "&.Mui-focused": {
            color: "#0E497A",
        },
    },
    "& .MuiInputBase-input": {
        fontFamily: "IBM Plex Sans, sans-serif",
        backgroundColor: "transparent",
        color: isSubmitted ? "#fff" : "#0E497A",
        "&.Mui-disabled": {
            color: isSubmitted ? "#fff" : "rgba(0, 0, 0, 0.38)",
            WebkitTextFillColor: isSubmitted ? "#fff" : "rgba(0, 0, 0, 0.38)",
        },
        "&::placeholder": {
            color: isSubmitted ? "rgba(255, 255, 255, 0.5)" : "rgba(14, 73, 122, 0.4)",
            fontStyle: "italic",
            opacity: 1,
        },
    },
    "& .MuiFormHelperText-root": {
        color: isSubmitted ? "#fff" : "#0E497A",
        fontFamily: "IBM Plex Sans, sans-serif",
        fontSize: "14px",
        marginLeft: 0,
        marginTop: "8px",
    },
    "& .MuiPaper-root": {
        width: "auto",
        minWidth: "fit-content",
    },
}));
const StepIndicator = styled(Typography)(({ theme }) => ({
    color: "#0E497A",
    fontFamily: "IBM Plex Sans, sans-serif",
    fontSize: "16px",
    fontWeight: 500,
    marginBottom: theme.spacing(2),
}));
const QuestionLabel = styled(Typography)(({ theme }) => ({
    color: "#0E497A",
    fontWeight: 600,
    fontFamily: "IBM Plex Sans, sans-serif",
    fontSize: "14px",
    marginBottom: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
}));
const HelperText = styled(Typography)(({ theme }) => ({
    color: "#1BBC9B",
    fontFamily: "IBM Plex Sans, sans-serif",
    fontSize: "12px",
    width: "100%",
    marginBottom: theme.spacing(1),
}));
// Add styles for the Select Menu
const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
    fontSize: "14px",
    fontFamily: "IBM Plex Sans, sans-serif",
    color: "#0E497A",
    padding: "10px 15px",
    "&.Mui-selected": {
        backgroundColor: "#F0F5F9",
    },
    "&:hover": {
        backgroundColor: "#F0F5F9",
    },
}));
const StyledRadioGroup = styled(RadioGroup)(({ theme }) => ({
    marginBottom: theme.spacing(3),
    width: "75%",
    "& .MuiFormControlLabel-root": {
        marginRight: theme.spacing(2),
    },
    "& .MuiRadio-root": {
        color: "#0E497A",
        "&.Mui-checked": {
            color: "#0E497A",
        },
        "&.Mui-disabled": {
            color: "#0E497A",
        },
    },
    "& .MuiTypography-root": {
        fontFamily: "IBM Plex Sans, sans-serif",
        fontSize: "14px",
    },
    "& .MuiFormControlLabel-root.Mui-disabled .MuiTypography-root": {
        color: "#0E497A",
    },
}));
const StyledSelect = styled("select")(({ theme }) => ({
    marginBottom: theme.spacing(3),
    width: "75%",
    height: "50px",
    borderRadius: "5px",
    border: "1px solid #D5DDE4",
    backgroundColor: "#fff",
    padding: "0 14px",
    fontFamily: "IBM Plex Sans, sans-serif",
    fontSize: "14px",
    color: "#0E497A",
    appearance: "none",
    "&:focus": {
        outline: "none",
        borderColor: "#0E497A",
    },
}));
const StyledDropdownTrigger = styled("div")(({ theme }) => ({
    marginBottom: theme.spacing(3),
    width: "75%",
    height: "50px",
    borderRadius: "5px",
    border: "1px solid #D5DDE4",
    backgroundColor: "#fff",
    padding: "0 14px",
    fontFamily: "IBM Plex Sans, sans-serif",
    fontSize: "14px",
    color: "#0E497A",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
    "&:focus": {
        outline: "none",
        borderColor: "#0E497A",
    },
}));
const StyledTextarea = styled("textarea")(({ theme, isSubmitted, isDisabled }) => ({
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    borderRadius: "5px",
    border: "1px solid #D5DDE4",
    "&:hover": {
        borderColor: "#D5DDE4",
    },
    "&:focus": {
        borderColor: "#0E497A",
        outline: "none",
    },
    padding: theme.spacing(1),
    fontFamily: "IBM Plex Sans, sans-serif",
    fontSize: "0.85rem",
    backgroundColor: isSubmitted ? "#0E497A" : "transparent",
    color: isDisabled || isSubmitted ? "#fff" : "#0E497A",
    "&:disabled": {
        backgroundColor: "#0E497A",
        color: "#fff",
        borderColor: "#0E497A",
        height: isSubmitted ? "auto" : undefined,
        minHeight: isSubmitted ? "120px" : undefined,
        overflowY: isSubmitted ? "auto" : undefined,
        overflowX: isSubmitted ? "hidden" : undefined,
        resize: isSubmitted ? "none" : undefined,
    },
    "&:disabled::-webkit-scrollbar": {
        width: "6px",
    },
    "&:disabled::-webkit-scrollbar-track": {
        background: "rgba(255, 255, 255, 0.1)",
        borderRadius: "3px",
    },
    "&:disabled::-webkit-scrollbar-thumb": {
        background: "rgba(255, 255, 255, 0.3)",
        borderRadius: "3px",
    },
    "&:disabled::-webkit-scrollbar-thumb:hover": {
        background: "rgba(255, 255, 255, 0.5)",
    },
    "&::placeholder": {
        color: isSubmitted ? "rgba(255, 255, 255, 0.5)" : "rgba(14, 73, 122, 0.4)",
        fontStyle: "italic",
        opacity: 1,
    },
}));
const DropdownItem = styled("div")(({ isSelected }) => ({
    padding: "10px 15px",
    cursor: "pointer",
    backgroundColor: isSelected ? "#F0F5F9" : "transparent",
    "&:hover": {
        backgroundColor: "#F0F5F9",
    },
    fontFamily: "IBM Plex Sans, sans-serif",
    fontSize: "14px",
    color: "#0E497A",
}));
const CheckboxContainer = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(3),
    width: "75%",
}));
const TextareaContainer = styled(Box)(({ theme }) => ({
    position: "relative",
    width: "100%",
    marginBottom: theme.spacing(3),
}));
const CloseButton = styled("button")(({ theme }) => ({
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#666",
    fontSize: "16px",
    padding: "0",
    marginLeft: "auto",
    "&:hover": {
        color: "#333",
    },
}));
// NEW: Styled tooltip icon
const TooltipIcon = styled(IconButton)(({ theme }) => ({
    padding: "4px",
    marginLeft: theme.spacing(0.5),
    color: "#0E497A",
    "&:hover": {
        backgroundColor: "rgba(14, 73, 122, 0.04)",
    },
}));
const TableContainer = styled(Box)(({ theme }) => ({
    backgroundColor: "#E8F4FD",
    borderRadius: "8px",
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
}));
const TableRow = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 1fr",
    gap: theme.spacing(2),
    alignItems: "center",
    marginBottom: theme.spacing(2),
    "&:first-of-type": {
        marginBottom: theme.spacing(3),
    },
}));
const TableHeader = styled(Typography)(({ theme }) => ({
    fontFamily: "IBM Plex Sans, sans-serif",
    fontSize: "14px",
    fontWeight: 600,
    color: "#0E497A",
    marginBottom: theme.spacing(1),
}));
const TableSelect = styled(Select)(({ theme }) => ({
    width: "100%",
    height: "50px",
    backgroundColor: "#fff",
    borderRadius: "5px",
    fontFamily: "IBM Plex Sans, sans-serif",
    fontSize: "14px",
    "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "#D5DDE4",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#0E497A",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#0E497A",
    },
    "& .MuiSelect-select": {
        padding: "14px",
        color: "#0E497A",
        display: "flex",
        alignItems: "center",
    },
    "& .MuiSelect-icon": {
        color: "#0E497A",
    },
}));
const TableTextField = styled(TextField)(({ theme }) => ({
    width: "100%",
    "& .MuiOutlinedInput-root": {
        height: "50px",
        backgroundColor: "#fff",
        borderRadius: "5px",
        fontFamily: "IBM Plex Sans, sans-serif",
        fontSize: "14px",
        "& fieldset": {
            borderColor: "#D5DDE4",
        },
        "&:hover fieldset": {
            borderColor: "#0E497A",
        },
        "&.Mui-focused fieldset": {
            borderColor: "#0E497A",
        },
    },
    "& .MuiInputBase-input": {
        padding: "14px",
        color: "#0E497A",
    },
}));
const AddRowButton = styled(Button)(({ theme }) => ({
    backgroundColor: "#1BBC9B",
    color: "#fff",
    fontFamily: "IBM Plex Sans, sans-serif",
    fontSize: "14px",
    fontWeight: 500,
    textTransform: "none",
    borderRadius: "25px",
    padding: "8px 20px",
    boxShadow: "none",
    "&:hover": {
        backgroundColor: "#17a085",
        boxShadow: "none",
    },
    "& .MuiButton-startIcon": {
        marginRight: theme.spacing(1),
    },
}));
const ADDITIONAL_CONTEXT_LABEL = "Additional Context";
const ADDITIONAL_CONTEXT_HELPER = "Any additional context related to the audience, objective or the attached document.";
const ADDITIONAL_CONTEXT_RED = "Make sure the context provided is aligned with the attached document";
const injectAdditionalContextField = (fields) => {
    const hasTopic = Object.values(fields).some((f) => f.label?.toLowerCase() === "topic");
    const hasSubTopic = Object.values(fields).some((f) => f.label?.toLowerCase() === "sub-topic");
    if (hasTopic || hasSubTopic) {
        return fields;
    }
    const existingKey = Object.keys(fields).find((key) => fields[key].label === ADDITIONAL_CONTEXT_LABEL);
    if (existingKey) {
        const helperText = fields[existingKey].helperText || "";
        const alreadyHasRed = helperText.includes(ADDITIONAL_CONTEXT_RED);
        return {
            ...fields,
            [existingKey]: {
                ...fields[existingKey],
                helperText: alreadyHasRed
                    ? helperText
                    : helperText
                        ? `${helperText}\n${ADDITIONAL_CONTEXT_RED}`
                        : `${ADDITIONAL_CONTEXT_HELPER}\n${ADDITIONAL_CONTEXT_RED}`,
            },
        };
    }
    else {
        return {
            ...fields,
            additional_context: {
                type: "textarea",
                label: ADDITIONAL_CONTEXT_LABEL,
                required: false,
                helperText: `${ADDITIONAL_CONTEXT_HELPER}\n${ADDITIONAL_CONTEXT_RED}`,
                placeholder: "Enter any additional context...",
            },
        };
    }
};
const Form = ({ formTitle: propFormTitle, formFields: propFormFields, formId: propFormId, styles = {}, sessionId, onSubmit, onError, onCancel, isExpanded = false, fileData, onFileRemove, summary, hideAdditionalContextField = false, onPdfUpload, enableReviewMode: propEnableReviewMode, submitButtonText: propSubmitButtonText, editButtonText: propEditButtonText = "Edit", confirmButtonText: propConfirmButtonText = "Confirm", form, // NEW: Backend form object
forceSubmitted, forceConfirmed, }) => {
    // NEW: Extract values from form object if provided, otherwise use direct props
    const formTitle = form?.form_title || propFormTitle || "";
    const formFields = form?.form_fields || propFormFields || {};
    const formId = form?.form_id || propFormId || "";
    const enableReviewMode = form?.enableReviewMode !== undefined
        ? form.enableReviewMode
        : (propEnableReviewMode !== undefined ? propEnableReviewMode : true);
    const submitButtonText = form?.submitButtonText || propSubmitButtonText;
    const editButtonText = form?.editButtonText || propEditButtonText || "Edit";
    const confirmButtonText = form?.confirmButtonText || propConfirmButtonText || "Confirm";
    const injectedFormFields = useMemo(() => {
        if (hideAdditionalContextField)
            return formFields;
        return injectAdditionalContextField(formFields);
    }, [formFields, hideAdditionalContextField]);
    const [formData, setFormData] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [isVisible, setIsVisible] = useState(true);
    const [dropdownAnchors, setDropdownAnchors] = useState({});
    const [customOptions, setCustomOptions] = useState({});
    const [customInputValue, setCustomInputValue] = useState("");
    const fileInputRef = React.useRef(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [isUploadingPdf, setIsUploadingPdf] = useState(false);
    const [tableData, setTableData] = useState([]);
    const configField = Object.values(injectedFormFields).find((field) => field.type === "config");
    // Use extracted submitButtonText if provided, otherwise use button_name from config or default
    const defaultSubmitButtonName = configField?.button_name || "Next";
    const finalSubmitButtonText = submitButtonText || defaultSubmitButtonName;
    const isReviewType = configField?.form_type === "review";
    const disabledMap = useMemo(() => {
        const map = {};
        Object.entries(injectedFormFields).forEach(([id, f]) => {
            if (!f.depends_on)
                map[id] = false;
            else {
                const parentEntry = Object.entries(injectedFormFields).find(([pid, pf]) => pf.label === f.depends_on);
                map[id] = !(parentEntry && formData[parentEntry[0]]);
            }
        });
        return map;
    }, [formData, injectedFormFields]);
    const shouldUseGrid = useMemo(() => {
        if (isReviewType) {
            return false;
        }
        const fieldCount = Object.keys(injectedFormFields).filter((key) => injectedFormFields[key].type !== "config").length;
        console.log("Grid calculation:", {
            isExpanded,
            fieldCount,
            shouldUse: Boolean(isExpanded && fieldCount > 3),
        });
        return Boolean(isExpanded && fieldCount > 3);
    }, [isExpanded, injectedFormFields, isReviewType]);
    useEffect(() => {
        console.log("formData", formData);
    }, [formData]);
    useEffect(() => {
        console.log("Grid mode debug:", {
            isExpanded,
            fieldsCount: Object.keys(injectedFormFields).length,
            shouldUseGrid,
        });
    }, [isExpanded, injectedFormFields, shouldUseGrid]);
    useEffect(() => {
        const defaults = {};
        Object.entries(injectedFormFields).forEach(([fieldId, field]) => {
            defaults[fieldId] = field.defaultValue || "";
        });
        setFormData(defaults);
    }, []);
    useEffect(() => {
        const hasTableField = Object.values(injectedFormFields).some(field => field.type === "table");
        if (hasTableField && tableData.length === 0) {
            setTableData([
                { topic: '', blooms: '', difficulty: '', item_type: '', items_required: '' },
                { topic: '', blooms: '', difficulty: '', item_type: '', items_required: '' }
            ]);
        }
    }, [injectedFormFields, tableData.length]);
    const [isSubmitted, setIsSubmitted] = useState(form?.enableReviewMode === false ? false : (forceSubmitted || false));
    useEffect(() => {
        if (forceSubmitted) {
            setIsSubmitted(true);
        }
    }, [forceSubmitted]);
    const [isConfirmed, setIsConfirmed] = useState(forceConfirmed || false);
    useEffect(() => {
        if (forceConfirmed) {
            setIsConfirmed(true);
        }
    }, [forceConfirmed]);
    const validateField = (field, value) => {
        if (field.required && !value) {
            return `${field.label} is required`;
        }
        if (field.validation && value) {
            if (field.type === "number") {
                const numVal = Number(value);
                if (field.validation?.min !== undefined &&
                    numVal < field.validation.min) {
                    return `Value must be at least ${field.validation.min}`;
                }
                if (field.validation?.max !== undefined &&
                    numVal > field.validation.max) {
                    return `Value must be at most ${field.validation.max}`;
                }
                if (field.limit !== undefined && numVal > field.limit) {
                    return `Value must be at most ${field.limit}`;
                }
            }
            if (field.validation.pattern) {
                const re = new RegExp(field.validation.pattern);
                if (!re.test(value)) {
                    return field.validation.message || "Invalid format";
                }
            }
        }
        return "";
    };
    const handleChange = (fieldId, field, value) => {
        setFormData((prev) => {
            const updated = { ...prev, [fieldId]: value };
            Object.entries(injectedFormFields).forEach(([id, f]) => {
                if (f.depends_on === field.label) {
                    updated[id] = "";
                    Object.entries(injectedFormFields).forEach(([subId, subF]) => {
                        if (subF.depends_on === f.label) {
                            updated[subId] = "";
                        }
                    });
                }
            });
            return updated;
        });
        const newError = validateField(field, value);
        setErrors((prev) => {
            const updatedErrors = { ...prev, [fieldId]: newError };
            Object.entries(injectedFormFields).forEach(([id, f]) => {
                if (f.depends_on === field.label) {
                    delete updatedErrors[id];
                    Object.entries(injectedFormFields).forEach(([subId, subF]) => {
                        if (subF.depends_on === f.label) {
                            delete updatedErrors[subId];
                        }
                    });
                }
            });
            return updatedErrors;
        });
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting)
            return; // hard guard
        setIsSubmitting(true); // lock immediately
        console.log("Form Data", formData);
        console.log("Table Data", tableData);
        const newErrors = {};
        const hasTableField = Object.values(injectedFormFields).some(field => field.type === "table");
        if (hasTableField) {
            const topFormFields = ['subject', 'distractors', 'language'];
            topFormFields.forEach(fieldKey => {
                if (formData && !formData[fieldKey] || String(formData[fieldKey]).trim() === '') {
                    newErrors[fieldKey] = `${fieldKey.charAt(0).toUpperCase() + fieldKey.slice(1)} is required`;
                }
            });
            if (tableData.length > 0) {
                const tableErrors = validateTableData();
                if (tableErrors.length > 0) {
                    newErrors['table'] = `Please fill all required table fields: ${tableErrors.join(', ')}`;
                }
            }
            else {
                newErrors['table'] = 'Table data is required';
            }
        }
        else {
            Object.entries(injectedFormFields).forEach(([fieldId, field]) => {
                if (field.type === "config")
                    return;
                const err = validateField(field, formData[fieldId]);
                if (err) {
                    newErrors[fieldId] = err;
                }
                if (field.type === "number" &&
                    field.limit !== undefined &&
                    formData[fieldId]) {
                    const numVal = Number(formData[fieldId]);
                    if (numVal > field.limit) {
                        newErrors[fieldId] = `Value must be at most ${field.limit}`;
                    }
                }
            });
        }
        if (Object.keys(newErrors).length) {
            console.log("Found Errors", newErrors);
            setErrors(newErrors);
            setIsSubmitting(false); // unlock on validation failure
            return;
        }
        // NEW: Check if review mode is enabled
        if (enableReviewMode) {
            // Mark form as submitted (review state) - NOT sending to server yet
            setIsSubmitted(true);
            setIsSubmitting(false);
            // Resize textareas to fit content
            setTimeout(() => {
                document.querySelectorAll("textarea").forEach((textarea) => {
                    if (textarea.scrollHeight > 0) {
                        textarea.style.height = "auto";
                        textarea.style.height = Math.min(textarea.scrollHeight, 80) + "px";
                    }
                });
            }, 100);
        }
        else {
            // If review mode is disabled, submit directly
            await handleConfirm();
        }
    };
    const handleEdit = () => {
        setIsSubmitted(false);
        setErrors({});
    };
    const handleConfirm = async () => {
        try {
            if (isConfirmed)
                return;
            setIsConfirmed(true);
            console.log("Document: ", fileData, summary);
            let finalFormData = { ...formData };
            const additionalContextKey = Object.keys(injectedFormFields).find((key) => injectedFormFields[key].label === ADDITIONAL_CONTEXT_LABEL);
            if (summary && additionalContextKey) {
                const userContext = formData[additionalContextKey] || "";
                if (!userContext.trim()) {
                    finalFormData[additionalContextKey] = summary;
                }
            }
            const hasTableField = Object.values(injectedFormFields).some(field => field.type === "table");
            let formResponse;
            if (hasTableField && tableData.length > 0) {
                const enrichedTableData = tableData.map(row => ({
                    ...row,
                    subject: formData.subject || '',
                    distractors: formData.distractors || '',
                    language: formData.language || ''
                }));
                formResponse = {
                    form_id: formId,
                    form_data: JSON.stringify(enrichedTableData),
                };
            }
            else {
                formResponse = {
                    form_id: formId,
                    form_data: finalFormData,
                };
            }
            console.log("Final Form Response:", formResponse);
            onSubmit?.(formResponse);
        }
        catch (err) {
            onError?.(err);
            setIsConfirmed(false);
        }
    };
    const handleCancel = () => {
        setIsVisible(false);
        onCancel?.();
        setFormData({});
        setErrors({});
    };
    const handleAddNewOption = async (fieldId, field, value) => {
        try {
            const cfg = field.addNewConfig;
            if (cfg) {
                const payload = { ...cfg.bodyTemplate };
                const labelKey = field.label.toLowerCase();
                const setFieldInPayload = (key, val) => {
                    const match = Object.keys(payload).find((k) => k.toLowerCase() === key.toLowerCase());
                    if (match)
                        payload[match] = val;
                };
                if (labelKey === "subject") {
                    setFieldInPayload("subject", value);
                }
                else if (labelKey === "topic") {
                    const subjectEntry = Object.entries(injectedFormFields).find(([k, f]) => k !== "config" && f.label.toLowerCase() === "subject");
                    const subjectVal = subjectEntry ? formData[subjectEntry[0]] : "";
                    setFieldInPayload("subject", subjectVal);
                    setFieldInPayload("topic", value);
                }
                else if (labelKey === "sub-topic") {
                    const entries = Object.entries(injectedFormFields);
                    const subjectEntry = entries.find(([k, f]) => k !== "config" && f.label.toLowerCase() === "subject");
                    const topicEntry = entries.find(([k, f]) => k !== "config" && f.label.toLowerCase() === "topic");
                    const subjectVal = subjectEntry ? formData[subjectEntry[0]] : "";
                    const topicVal = topicEntry ? formData[topicEntry[0]] : "";
                    setFieldInPayload("subject", subjectVal);
                    setFieldInPayload("topic", topicVal);
                    setFieldInPayload("subtopic", value);
                }
                else {
                    for (const key in payload) {
                        if (payload[key] === "") {
                            payload[key] = value;
                            break;
                        }
                    }
                }
                setCustomOptions((prev) => ({
                    ...prev,
                    [fieldId]: [...(prev[fieldId] || []), { label: value, value }],
                }));
                handleChange(fieldId, field, value);
                console.log("Payload", payload);
                console.log("Config", cfg);
                await fetch(cfg.url, {
                    method: cfg.method,
                    headers: cfg.headers,
                    body: JSON.stringify(payload),
                });
            }
        }
        catch (err) {
            console.error("Error adding custom option:", err);
        }
        finally {
            handleCloseDropdown(fieldId);
        }
    };
    const getOptions = (fieldId, field) => {
        let baseOptions = [];
        if (field.options) {
            if (!field.depends_on) {
                baseOptions = field.options;
            }
            else {
                const parentEntry = Object.entries(injectedFormFields).find(([, f]) => f.label === field.depends_on);
                if (parentEntry) {
                    const parentId = parentEntry[0];
                    const parentValue = formData[parentId];
                    if (parentValue) {
                        baseOptions = field.options.filter((opt) => {
                            if (opt.key === parentValue)
                                return true;
                            if (field.label.toLowerCase() === "sub-topic" &&
                                field.depends_on &&
                                field.depends_on.toLowerCase() === "topic") {
                                return opt.key === parentValue;
                            }
                            return false;
                        });
                    }
                }
            }
        }
        const dynamic = customOptions[fieldId] || [];
        return [...baseOptions, ...dynamic];
    };
    const handleTriggerClick = (fieldId, event) => {
        setDropdownAnchors((prev) => ({
            ...prev,
            [fieldId]: prev[fieldId] ? null : event.currentTarget,
        }));
    };
    const handleCloseDropdown = (fieldId) => {
        setDropdownAnchors((prev) => ({ ...prev, [fieldId]: null }));
    };
    const handleAttachmentClick = () => {
        fileInputRef.current?.click();
    };
    const isFieldDisabled = (field) => {
        if (!field.depends_on)
            return false;
        const parentEntry = Object.entries(injectedFormFields).find(([, f]) => f.label === field.depends_on);
        if (!parentEntry)
            return false;
        const parentValue = formData[parentEntry[0]];
        return !parentValue;
    };
    const handleCaptureEvent = (e) => {
        const eventType = e.type;
        console.log(`Captured event '${eventType}'`, {
            key: e.key,
            type: eventType,
            target: e.target,
            currentTarget: e.currentTarget,
            nativeEvent: e.nativeEvent,
        });
    };
    const handleTableCellChange = (rowIndex, columnKey, value) => {
        setTableData(prev => prev.map((row, index) => index === rowIndex ? { ...row, [columnKey]: value } : row));
    };
    const handleFormFieldChange = (fieldKey, value) => {
        setFormData(prev => ({
            ...prev,
            [fieldKey]: value
        }));
    };
    const addTableRow = () => {
        if (tableData.length < 10) {
            setTableData(prev => [
                ...prev,
                { topic: '', blooms: '', difficulty: '', item_type: '', items_required: '' }
            ]);
        }
    };
    const deleteTableRow = (rowIndex) => {
        if (tableData.length > 2) {
            setTableData(prev => prev.filter((_, index) => index !== rowIndex));
        }
        else if (tableData.length === 2) {
            setTableData(prev => prev.map((row, index) => index === rowIndex
                ? { topic: '', blooms: '', difficulty: '', item_type: '', items_required: '' }
                : row));
        }
    };
    const validateTableData = () => {
        const errors = [];
        const requiredFields = ['topic', 'blooms', 'difficulty', 'item_type', 'items_required'];
        const MAX_ITEMS_PER_ROW = 25;
        const MAX_TOTAL_ITEMS = 250;
        let totalItems = 0;
        let completeRows = 0;
        tableData.forEach((row, index) => {
            const isEmptyRow = requiredFields.every(field => !row[field] || String(row[field]).trim() === '');
            const isCompleteRow = requiredFields.every(field => row[field] && String(row[field]).trim() !== '');
            if (isCompleteRow) {
                completeRows++;
                if (row['items_required']) {
                    const itemsValue = parseInt(row['items_required']);
                    if (!isNaN(itemsValue)) {
                        if (itemsValue > MAX_ITEMS_PER_ROW) {
                            errors.push(`Row ${index + 1} - items required cannot exceed ${MAX_ITEMS_PER_ROW}`);
                        }
                        totalItems += itemsValue;
                    }
                }
            }
            else if (!isEmptyRow) {
                const missingFields = requiredFields.filter(field => !row[field] || String(row[field]).trim() === '');
                errors.push(`Row ${index + 1} - incomplete (missing: ${missingFields.join(', ').replace(/_/g, ' ')})`);
            }
        });
        if (completeRows < 2) {
            errors.push('At least two complete rows are required');
        }
        if (totalItems > MAX_TOTAL_ITEMS) {
            errors.push(`Total items (${totalItems}) exceeds maximum of ${MAX_TOTAL_ITEMS}`);
        }
        return errors;
    };
    const isFormValid = useMemo(() => {
        const hasTableField = Object.values(injectedFormFields).some(field => field.type === "table");
        if (hasTableField) {
            const topFormFields = ['subject', 'distractors', 'language'];
            for (const fieldKey of topFormFields) {
                if (formData && !formData[fieldKey] || String(formData[fieldKey]).trim() === '') {
                    return false;
                }
            }
            if (tableData.length > 0) {
                const tableErrors = validateTableData();
                return tableErrors.length === 0;
            }
            return false;
        }
        for (const [fieldId, field] of Object.entries(injectedFormFields)) {
            if (field.type === "config")
                continue;
            if (field.required && (!formData[fieldId] || String(formData[fieldId]).trim() === '')) {
                return false;
            }
            const error = validateField(field, formData[fieldId] || '');
            if (error) {
                return false;
            }
        }
        return true;
    }, [tableData, injectedFormFields, formData]);
    // Helper function to determine if a field should be shown in review mode
    const shouldShowField = (fieldId, field) => {
        // Always show in edit mode
        if (!isSubmitted)
            return true;
        // In review mode, hide empty non-required fields with no placeholder
        if (isSubmitted) {
            const fieldValue = formData[fieldId];
            const hasValue = fieldValue && String(fieldValue).trim() !== '';
            // Show if field is required OR has a value OR has a placeholder
            if (field.required || hasValue || field.placeholder) {
                return true;
            }
            // Hide empty non-required fields without placeholder
            return false;
        }
        return true;
    };
    // NEW: Helper function to render tooltip icon if tooltip exists
    const renderTooltipIcon = (tooltip) => {
        if (!tooltip)
            return null;
        console.log("Tooltip text:", tooltip); // Debug log
        return (_jsx(Tooltip, { title: tooltip, arrow: true, placement: "right", open: undefined, PopperProps: {
                style: { zIndex: 999999 }
            }, children: _jsx(IconButton, { size: "small", style: {
                    padding: "4px",
                    marginLeft: "4px",
                    color: "#0E497A",
                }, onMouseEnter: (e) => {
                    console.log("Mouse entered tooltip icon");
                }, children: _jsx(FaInfoCircle, { size: 14 }) }) }));
    };
    const renderFormFields = () => {
        console.log("Rendering form fields:", {
            shouldUseGrid,
            isExpanded,
            isReviewType,
        });
        const tableFields = Object.entries(injectedFormFields).filter(([_, field]) => field.type === "table");
        if (tableFields.length > 0) {
            return tableFields.map(([fieldId, field]) => {
                if (field.type === "table") {
                    return (_jsx(TableField, { fieldId: fieldId, tableData: tableData, formData: formData, formFields: injectedFormFields, onTableCellChange: handleTableCellChange, onFormFieldChange: handleFormFieldChange, onAddTableRow: addTableRow, onDeleteTableRow: deleteTableRow, isSubmitted: isSubmitted }, fieldId));
                }
                return null;
            });
        }
        if (!shouldUseGrid) {
            return Object.entries(injectedFormFields).map(([fieldId, field]) => {
                if (field.type === "config") {
                    return null;
                }
                const useSideBySideLayout = isReviewType && (field.type === "select" || field.type === "number");
                return (_jsx(Box, { sx: { marginBottom: 2 }, children: useSideBySideLayout ? (_jsxs(Box, { sx: { display: "flex", alignItems: "flex-start", gap: 2 }, children: [_jsxs(QuestionLabel, { sx: { minWidth: "200px", marginBottom: 0, marginTop: "14px" }, children: [field.label, field.required && (_jsx("span", { style: { color: "#d32f2f", marginLeft: 4 }, children: "*" })), renderTooltipIcon(field.tooltip)] }), _jsx(Box, { sx: { flex: 1 }, children: field.type === "select" ? (_jsxs(StyledTextField, { isSubmitted: isSubmitted, isDisabled: disabledMap[fieldId] || isSubmitted, SelectProps: {
                                        MenuProps: {
                                            style: {
                                                zIndex: 1000000,
                                            },
                                            PaperProps: { style: { maxHeight: 300 } },
                                        },
                                        sx: isSubmitted
                                            ? {
                                                color: "#fff",
                                                ".MuiSvgIcon-root": { color: "#fff" },
                                            }
                                            : undefined,
                                    }, select: true, fullWidth: true, disabled: disabledMap[fieldId] || isSubmitted, value: formData[fieldId] || "", onChange: (e) => handleChange(fieldId, field, e.target.value), error: !!errors[fieldId], helperText: errors[fieldId] || "", required: field.required, variant: "outlined", children: [getOptions(fieldId, field).map((option) => (_jsx(StyledMenuItem, { value: option.value || option.label, children: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', width: '100%' }, children: [option.label, renderTooltipIcon(option.tooltip)] }) }, option.value || option.label))), field.allowAddNew && (_jsx(StyledMenuItem, { disableRipple: true, onMouseDown: (e) => e.stopPropagation(), onClick: (e) => e.stopPropagation(), sx: {
                                                position: "sticky",
                                                bottom: 0,
                                                backgroundColor: "#fff",
                                            }, children: _jsx(TextField, { fullWidth: true, variant: "outlined", size: "small", placeholder: `Add ${field.label}`, onKeyDownCapture: (e) => {
                                                    if (e.key === "Enter") {
                                                        const inputValue = e.target.value.trim();
                                                        if (inputValue) {
                                                            handleAddNewOption(fieldId, field, inputValue);
                                                            e.target.value = "";
                                                        }
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                    }
                                                }, onMouseDown: (e) => e.stopPropagation(), onClick: (e) => e.stopPropagation(), onKeyUp: (e) => e.stopPropagation(), InputProps: {
                                                    onKeyDown: (e) => {
                                                        e.stopPropagation();
                                                    },
                                                } }) }))] })) : (_jsx(StyledTextField, { isSubmitted: isSubmitted, isDisabled: disabledMap[fieldId] || isSubmitted, placeholder: `Enter ${field.label.toLowerCase()}`, type: field.type === "number" ? "number" : "text", value: formData[fieldId] || "", onChange: (e) => handleChange(fieldId, field, e.target.value), error: !!errors[fieldId], helperText: errors[fieldId] || "", required: field.required, variant: "outlined", fullWidth: true, inputProps: field.type === "number" && field.limit !== undefined
                                        ? { max: field.limit }
                                        : undefined, sx: {
                                        backgroundColor: isSubmitted
                                            ? "#0E497A"
                                            : "transparent",
                                        color: disabledMap[fieldId] || isSubmitted
                                            ? "#fff"
                                            : undefined,
                                        "& .MuiOutlinedInput-root fieldset": {
                                            borderColor: isSubmitted ? "#0E497A" : undefined,
                                        },
                                        "& .MuiInputBase-input": {
                                            color: disabledMap[fieldId] || isSubmitted
                                                ? "#fff"
                                                : "#0E497A",
                                            fontFamily: "IBM Plex Sans, sans-serif",
                                        },
                                    } })) })] })) : (_jsxs(_Fragment, { children: [_jsxs(QuestionLabel, { children: [field.label, field.required && (_jsx("span", { style: { color: "#d32f2f", marginLeft: 4 }, children: "*" })), renderTooltipIcon(field.tooltip)] }), field.helperText
                                ? (() => {
                                    const lines = field.helperText.split("\n");
                                    const hasRed = lines[lines.length - 1] === ADDITIONAL_CONTEXT_RED;
                                    if (hasRed) {
                                        const mainText = lines.slice(0, -1).join(" ");
                                        return (_jsxs(HelperText, { children: [mainText, _jsx("br", {}), _jsxs("span", { style: { color: "#d32f2f" }, children: ["(", ADDITIONAL_CONTEXT_RED, ")"] })] }));
                                    }
                                    else {
                                        return _jsx(HelperText, { children: lines.join(" ") });
                                    }
                                })()
                                : null, field.type === "radio" ? (_jsx(StyledRadioGroup, { value: formData[fieldId] || "", onChange: (e) => handleChange(fieldId, field, e.target.value), children: field.options?.map((option) => (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center' }, children: [_jsx(FormControlLabel, { value: option.value || option.label, control: _jsx(Radio, { disabled: isSubmitted }), label: option.label, disabled: isSubmitted }), renderTooltipIcon(option.tooltip)] }, option.value || option.label))) })) : field.type === "boolean" ? (_jsxs(StyledRadioGroup, { value: formData[fieldId] || "", onChange: (e) => handleChange(fieldId, field, e.target.value), children: [_jsx(FormControlLabel, { value: "true", control: _jsx(Radio, { disabled: isSubmitted }), label: "Yes", disabled: isSubmitted }), _jsx(FormControlLabel, { value: "false", control: _jsx(Radio, { disabled: isSubmitted }), label: "No", disabled: isSubmitted })] })) : field.type === "select" ? (_jsxs(StyledTextField, { isSubmitted: isSubmitted, isDisabled: disabledMap[fieldId] || isSubmitted, SelectProps: {
                                    MenuProps: {
                                        style: {
                                            zIndex: 1000000,
                                        },
                                        PaperProps: { style: { maxHeight: 300 } },
                                    },
                                    sx: isSubmitted
                                        ? {
                                            color: "#fff",
                                            ".MuiSvgIcon-root": { color: "#fff" },
                                        }
                                        : undefined,
                                }, select: true, fullWidth: true, disabled: disabledMap[fieldId] || isSubmitted, value: formData[fieldId] || "", onChange: (e) => handleChange(fieldId, field, e.target.value), error: !!errors[fieldId], helperText: errors[fieldId] || "", required: field.required, variant: "outlined", children: [getOptions(fieldId, field).map((option) => (_jsx(StyledMenuItem, { value: option.value || option.label, children: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', width: '100%' }, children: [option.label, renderTooltipIcon(option.tooltip)] }) }, option.value || option.label))), field.allowAddNew && (_jsx(StyledMenuItem, { disableRipple: true, onMouseDown: (e) => e.stopPropagation(), onClick: (e) => e.stopPropagation(), sx: {
                                            position: "sticky",
                                            bottom: 0,
                                            backgroundColor: "#fff",
                                        }, children: _jsx(TextField, { fullWidth: true, variant: "outlined", size: "small", placeholder: `Add ${field.label}`, onKeyDownCapture: (e) => {
                                                if (e.key === "Enter") {
                                                    const inputValue = e.target.value.trim();
                                                    if (inputValue) {
                                                        handleAddNewOption(fieldId, field, inputValue);
                                                        e.target.value = "";
                                                    }
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                }
                                            }, onMouseDown: (e) => e.stopPropagation(), onClick: (e) => e.stopPropagation(), onKeyUp: (e) => e.stopPropagation(), InputProps: {
                                                onKeyDown: (e) => {
                                                    e.stopPropagation();
                                                },
                                            } }) }))] })) : field.type === "textarea" ? (_jsxs(TextareaContainer, { children: [_jsx(StyledTextarea, { isSubmitted: isSubmitted, isDisabled: disabledMap[fieldId] || isSubmitted, disabled: disabledMap[fieldId] || isSubmitted, placeholder: !isSubmitted
                                            ? field.placeholder ||
                                                `Enter ${field.label.toLowerCase()}`
                                            : "", rows: 5, sx: {
                                            height: "auto",
                                            resize: "none",
                                            minHeight: "120px",
                                            paddingBottom: field.attachment && (selectedFile || !isSubmitted) ? "50px" : undefined,
                                        }, value: formData[fieldId] || "", onChange: (e) => handleChange(fieldId, field, e.target.value), required: field.required }), field.attachment && (_jsxs(Box, { sx: {
                                            position: "absolute",
                                            bottom: "16px",
                                            left: "8px",
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            pointerEvents: "auto"
                                        }, children: [!isSubmitted && !selectedFile && !isUploadingPdf && (_jsx(IconButton, { onClick: handleAttachmentClick, size: "small", sx: {
                                                    color: isSubmitted ? "#fff" : "#0E497A",
                                                    padding: "4px",
                                                    "&:hover": { backgroundColor: "rgba(14, 73, 122, 0.1)" }
                                                }, children: _jsx(FaPaperclip, { size: 16 }) })), isUploadingPdf && (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, px: 1 }, children: [_jsx(CircularProgress, { size: 14, sx: { color: "#0E497A" } }), _jsx(Typography, { variant: "caption", sx: { color: "#0E497A", fontFamily: "IBM Plex Sans", fontSize: "11px" }, children: "Uploading..." })] })), selectedFile && !isUploadingPdf && (_jsxs(Box, { sx: {
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "space-between",
                                                    gap: 1,
                                                    px: 1,
                                                    py: 0.5,
                                                    backgroundColor: isSubmitted ? "rgba(14, 73, 122, 0.8)" : "rgba(240, 245, 249, 0.95)",
                                                    borderRadius: "4px",
                                                    border: isSubmitted ? "1px solid rgba(255, 255, 255, 0.3)" : "1px solid #D5DDE4",
                                                    flex: 1,
                                                    backdropFilter: "blur(4px)"
                                                }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 0.5, overflow: "hidden", minWidth: 0 }, children: [_jsx(FaFilePdf, { color: isSubmitted ? "#fff" : "#d32f2f", size: 14, style: { flexShrink: 0 } }), _jsx(Typography, { variant: "caption", sx: {
                                                                    fontFamily: "IBM Plex Sans, sans-serif",
                                                                    fontSize: "11px",
                                                                    color: isSubmitted ? "#fff" : "#0E497A",
                                                                    whiteSpace: "nowrap",
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis"
                                                                }, children: selectedFile.name })] }), !isSubmitted && (_jsx(IconButton, { size: "small", onClick: () => {
                                                            setSelectedFile(null);
                                                            if (fileInputRef.current) {
                                                                fileInputRef.current.value = "";
                                                            }
                                                            onFileRemove?.();
                                                        }, sx: {
                                                            padding: "2px",
                                                            color: "#666",
                                                            "&:hover": { color: "#d32f2f", backgroundColor: "rgba(211, 47, 47, 0.15)" }
                                                        }, children: _jsx(FaTimes, { size: 10 }) }))] }))] }))] })) : (_jsx(StyledTextField, { isSubmitted: isSubmitted, isDisabled: disabledMap[fieldId] || isSubmitted, placeholder: !isSubmitted ? `Enter ${field.label.toLowerCase()}` : "", type: field.type === "number" ? "number" : "text", value: formData[fieldId] || "", onChange: (e) => handleChange(fieldId, field, e.target.value), error: !!errors[fieldId], helperText: errors[fieldId] || "", required: field.required, variant: "outlined", fullWidth: true, inputProps: field.type === "number" && field.limit !== undefined
                                    ? { max: field.limit }
                                    : undefined, sx: {
                                    backgroundColor: isSubmitted ? "#0E497A" : "transparent",
                                    color: disabledMap[fieldId] || isSubmitted
                                        ? "#fff"
                                        : undefined,
                                    "& .MuiOutlinedInput-root fieldset": {
                                        borderColor: isSubmitted ? "#0E497A" : undefined,
                                    },
                                    "& .MuiInputBase-input": {
                                        color: disabledMap[fieldId] || isSubmitted
                                            ? "#fff"
                                            : "#0E497A",
                                        fontFamily: "IBM Plex Sans, sans-serif",
                                    },
                                } })), (field.type === "select" || field.type === "boolean") &&
                                errors[fieldId] && (_jsx(Typography, { variant: "caption", color: "error", sx: { mt: 1 }, children: errors[fieldId] }))] })) }, fieldId));
            });
        }
        return (_jsx(Grid, { container: true, spacing: 3, children: Object.entries(injectedFormFields).map(([fieldId, field]) => {
                if (field.type === "config") {
                    return null;
                }
                const useSideBySideLayout = isReviewType &&
                    (field.type === "select" || field.type === "number");
                return (_jsx(Grid, { item: true, xs: 12, md: 6, children: _jsx(Box, { sx: { marginBottom: 2 }, children: useSideBySideLayout ? (_jsxs(Box, { sx: { display: "flex", alignItems: "flex-start", gap: 2 }, children: [_jsxs(QuestionLabel, { sx: {
                                        minWidth: "200px",
                                        marginBottom: 0,
                                        marginTop: "14px",
                                    }, children: [field.label, field.required && (_jsx("span", { style: { color: "#d32f2f", marginLeft: 4 }, children: "*" })), renderTooltipIcon(field.tooltip)] }), _jsx(Box, { sx: { flex: 1 }, children: field.type === "select" ? (_jsxs(StyledTextField, { isSubmitted: isSubmitted, isDisabled: disabledMap[fieldId] || isSubmitted, SelectProps: {
                                            MenuProps: {
                                                style: {
                                                    zIndex: 1000000,
                                                },
                                                PaperProps: { style: { maxHeight: 300 } },
                                            },
                                            sx: isSubmitted
                                                ? {
                                                    color: "#fff",
                                                    ".MuiSvgIcon-root": { color: "#fff" },
                                                }
                                                : undefined,
                                        }, select: true, fullWidth: true, disabled: disabledMap[fieldId] || isSubmitted, value: formData[fieldId] || "", onChange: (e) => handleChange(fieldId, field, e.target.value), error: !!errors[fieldId], helperText: errors[fieldId] || "", required: field.required, variant: "outlined", children: [getOptions(fieldId, field).map((option) => (_jsx(StyledMenuItem, { value: option.value || option.label, children: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', width: '100%' }, children: [option.label, renderTooltipIcon(option.tooltip)] }) }, option.value || option.label))), field.allowAddNew && (_jsx(StyledMenuItem, { disableRipple: true, onMouseDown: (e) => e.stopPropagation(), onClick: (e) => e.stopPropagation(), sx: {
                                                    position: "sticky",
                                                    bottom: 0,
                                                    backgroundColor: "#fff",
                                                }, children: _jsx(TextField, { fullWidth: true, variant: "outlined", size: "small", placeholder: `Add ${field.label}`, onKeyDownCapture: (e) => {
                                                        if (e.key === "Enter") {
                                                            const inputValue = e.target.value.trim();
                                                            if (inputValue) {
                                                                handleAddNewOption(fieldId, field, inputValue);
                                                                e.target.value = "";
                                                            }
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                        }
                                                    }, onMouseDown: (e) => e.stopPropagation(), onClick: (e) => e.stopPropagation(), onKeyUp: (e) => e.stopPropagation(), InputProps: {
                                                        onKeyDown: (e) => {
                                                            e.stopPropagation();
                                                        },
                                                    } }) }))] })) : (_jsx(StyledTextField, { isSubmitted: isSubmitted, isDisabled: disabledMap[fieldId] || isSubmitted, placeholder: !isSubmitted ? `Enter ${field.label.toLowerCase()}` : "", type: field.type === "number" ? "number" : "text", value: formData[fieldId] || "", onChange: (e) => handleChange(fieldId, field, e.target.value), error: !!errors[fieldId], helperText: errors[fieldId] || "", required: field.required, variant: "outlined", fullWidth: true, sx: {
                                            backgroundColor: isSubmitted
                                                ? "#0E497A"
                                                : "transparent",
                                            color: disabledMap[fieldId] || isSubmitted
                                                ? "#fff"
                                                : undefined,
                                            "& .MuiOutlinedInput-root fieldset": {
                                                borderColor: isSubmitted ? "#0E497A" : undefined,
                                            },
                                            "& .MuiInputBase-input": {
                                                color: disabledMap[fieldId] || isSubmitted
                                                    ? "#fff"
                                                    : "#0E497A",
                                                fontFamily: "IBM Plex Sans, sans-serif",
                                            },
                                        } })) })] })) : (_jsxs(_Fragment, { children: [_jsxs(QuestionLabel, { children: [field.label, field.required && (_jsx("span", { style: { color: "#d32f2f", marginLeft: 4 }, children: "*" })), renderTooltipIcon(field.tooltip)] }), field.helperText
                                    ? (() => {
                                        const lines = field.helperText.split("\n");
                                        const hasRed = lines[lines.length - 1] === ADDITIONAL_CONTEXT_RED;
                                        if (hasRed) {
                                            const mainText = lines.slice(0, -1).join(" ");
                                            return (_jsxs(HelperText, { children: [mainText, _jsx("br", {}), _jsxs("span", { style: { color: "#d32f2f" }, children: ["(", ADDITIONAL_CONTEXT_RED, ")"] })] }));
                                        }
                                        else {
                                            return _jsx(HelperText, { children: lines.join(" ") });
                                        }
                                    })()
                                    : null, field.type === "radio" ? (_jsx(StyledRadioGroup, { value: formData[fieldId] || "", onChange: (e) => handleChange(fieldId, field, e.target.value), children: field.options?.map((option) => (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center' }, children: [_jsx(FormControlLabel, { value: option.value || option.label, control: _jsx(Radio, { disabled: isSubmitted }), label: option.label, disabled: isSubmitted }), renderTooltipIcon(option.tooltip)] }, option.value || option.label))) })) : field.type === "boolean" ? (_jsxs(StyledRadioGroup, { value: formData[fieldId] || "", onChange: (e) => handleChange(fieldId, field, e.target.value), children: [_jsx(FormControlLabel, { value: "true", control: _jsx(Radio, { disabled: isSubmitted }), label: "Yes", disabled: isSubmitted }), _jsx(FormControlLabel, { value: "false", control: _jsx(Radio, { disabled: isSubmitted }), label: "No", disabled: isSubmitted })] })) : field.type === "select" ? (_jsxs(StyledTextField, { isSubmitted: isSubmitted, isDisabled: disabledMap[fieldId] || isSubmitted, SelectProps: {
                                        MenuProps: {
                                            style: {
                                                zIndex: 1000000,
                                            },
                                            PaperProps: { style: { maxHeight: 300 } },
                                        },
                                        sx: isSubmitted
                                            ? {
                                                color: "#fff",
                                                ".MuiSvgIcon-root": { color: "#fff" },
                                            }
                                            : undefined,
                                    }, select: true, fullWidth: true, disabled: disabledMap[fieldId] || isSubmitted, value: formData[fieldId] || "", onChange: (e) => handleChange(fieldId, field, e.target.value), error: !!errors[fieldId], helperText: errors[fieldId] || "", required: field.required, variant: "outlined", children: [getOptions(fieldId, field).map((option) => (_jsx(StyledMenuItem, { value: option.value || option.label, children: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', width: '100%' }, children: [option.label, renderTooltipIcon(option.tooltip)] }) }, option.value || option.label))), field.allowAddNew && (_jsx(StyledMenuItem, { disableRipple: true, onMouseDown: (e) => e.stopPropagation(), onClick: (e) => e.stopPropagation(), sx: {
                                                position: "sticky",
                                                bottom: 0,
                                                backgroundColor: "#fff",
                                            }, children: _jsx(TextField, { fullWidth: true, variant: "outlined", size: "small", placeholder: `Add ${field.label}`, onKeyDownCapture: (e) => {
                                                    if (e.key === "Enter") {
                                                        const inputValue = e.target.value.trim();
                                                        if (inputValue) {
                                                            handleAddNewOption(fieldId, field, inputValue);
                                                            e.target.value = "";
                                                        }
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                    }
                                                }, onMouseDown: (e) => e.stopPropagation(), onClick: (e) => e.stopPropagation(), onKeyUp: (e) => e.stopPropagation(), InputProps: {
                                                    onKeyDown: (e) => {
                                                        e.stopPropagation();
                                                    },
                                                } }) }))] })) : field.type === "textarea" ? (_jsxs(TextareaContainer, { children: [_jsx(StyledTextarea, { isSubmitted: isSubmitted, isDisabled: disabledMap[fieldId] || isSubmitted, disabled: disabledMap[fieldId] || isSubmitted, placeholder: !isSubmitted
                                                ? field.placeholder ||
                                                    `Enter ${field.label.toLowerCase()}`
                                                : "", rows: 5, sx: {
                                                height: "auto",
                                                resize: "none",
                                                minHeight: "120px",
                                                paddingBottom: field.attachment && (selectedFile || !isSubmitted) ? "50px" : undefined,
                                            }, value: formData[fieldId] || "", onChange: (e) => handleChange(fieldId, field, e.target.value), required: field.required }), field.attachment && (_jsxs(Box, { sx: {
                                                position: "absolute",
                                                bottom: "8px",
                                                left: "8px",
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                pointerEvents: "auto",
                                                zIndex: 10
                                            }, children: [_jsx(Tooltip, { title: "Attach a file (Max 1 document, 20MB limit)", arrow: true, placement: "top", children: _jsx("span", { style: { display: "inline-flex" }, children: _jsx(IconButton, { onClick: handleAttachmentClick, size: "small", title: "Attach a file (Max 1 document, 20MB limit)", sx: {
                                                                color: isSubmitted ? "#fff" : "#0E497A",
                                                                padding: "4px",
                                                                "&:hover": { backgroundColor: "rgba(14, 73, 122, 0.1)" }
                                                            }, children: _jsx(FaPaperclip, { size: 16 }) }) }) }), isUploadingPdf && (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, px: 1 }, children: [_jsx(CircularProgress, { size: 14, sx: { color: "#0E497A" } }), _jsx(Typography, { variant: "caption", sx: { color: "#0E497A", fontFamily: "IBM Plex Sans", fontSize: "11px" }, children: "Uploading..." })] })), selectedFile && !isUploadingPdf && (_jsxs(Box, { sx: {
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "space-between",
                                                        gap: 1,
                                                        px: 1,
                                                        py: 0.5,
                                                        backgroundColor: isSubmitted ? "rgba(14, 73, 122, 0.8)" : "rgba(240, 245, 249, 0.95)",
                                                        borderRadius: "4px",
                                                        border: isSubmitted ? "1px solid rgba(255, 255, 255, 0.3)" : "1px solid #D5DDE4",
                                                        flex: 1,
                                                        backdropFilter: "blur(4px)"
                                                    }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 0.5, overflow: "hidden", minWidth: 0 }, children: [_jsx(FaFilePdf, { color: isSubmitted ? "#fff" : "#d32f2f", size: 14, style: { flexShrink: 0 } }), _jsx(Typography, { variant: "caption", sx: {
                                                                        fontFamily: "IBM Plex Sans, sans-serif",
                                                                        fontSize: "11px",
                                                                        color: isSubmitted ? "#fff" : "#0E497A",
                                                                        whiteSpace: "nowrap",
                                                                        overflow: "hidden",
                                                                        textOverflow: "ellipsis"
                                                                    }, children: selectedFile.name })] }), !isSubmitted && (_jsx(IconButton, { size: "small", onClick: () => {
                                                                setSelectedFile(null);
                                                                if (fileInputRef.current) {
                                                                    fileInputRef.current.value = "";
                                                                }
                                                                onFileRemove?.();
                                                            }, sx: {
                                                                padding: "2px",
                                                                color: "#666",
                                                                "&:hover": { color: "#d32f2f", backgroundColor: "rgba(211, 47, 47, 0.15)" }
                                                            }, children: _jsx(FaTimes, { size: 10 }) }))] }))] }))] })) : (_jsx(StyledTextField, { isSubmitted: isSubmitted, isDisabled: disabledMap[fieldId] || isSubmitted, placeholder: !isSubmitted ? `Enter ${field.label.toLowerCase()}` : "", type: field.type === "number" ? "number" : "text", value: formData[fieldId] || "", onChange: (e) => handleChange(fieldId, field, e.target.value), error: !!errors[fieldId], helperText: errors[fieldId] || "", required: field.required, variant: "outlined", fullWidth: true, inputProps: field.type === "number" && field.limit !== undefined
                                        ? { max: field.limit }
                                        : undefined, sx: {
                                        backgroundColor: isSubmitted
                                            ? "#0E497A"
                                            : "transparent",
                                        color: disabledMap[fieldId] || isSubmitted
                                            ? "#fff"
                                            : undefined,
                                        "& .MuiOutlinedInput-root fieldset": {
                                            borderColor: isSubmitted ? "#0E497A" : undefined,
                                        },
                                        "& .MuiInputBase-input": {
                                            color: disabledMap[fieldId] || isSubmitted
                                                ? "#fff"
                                                : "#0E497A",
                                            fontFamily: "IBM Plex Sans, sans-serif",
                                        },
                                    } })), (field.type === "select" || field.type === "boolean") &&
                                    errors[fieldId] && (_jsx(Typography, { variant: "caption", color: "error", sx: { mt: 1 }, children: errors[fieldId] }))] })) }) }, fieldId));
            }) }));
    };
    return isVisible ? (_jsxs(StyledPaper, { onKeyDownCapture: handleCaptureEvent, onKeyPressCapture: handleCaptureEvent, onKeyUpCapture: handleCaptureEvent, onClickCapture: handleCaptureEvent, style: {
            ...styles.form,
            width: isExpanded ? "100%" : styles.form?.width,
            maxWidth: isExpanded ? "100%" : styles.form?.maxWidth,
        }, elevation: 0, children: [_jsx("input", { type: "file", ref: fileInputRef, accept: ".pdf,application/pdf", style: { display: "none" }, onChange: async (e) => {
                    const file = e.target.files?.[0];
                    if (file && file.size > 20 * 1024 * 1024) {
                        alert("File must be less than 20MB");
                        e.target.value = "";
                        return;
                    }
                    setSelectedFile(file || null);
                    if (file) {
                        setIsUploadingPdf(true);
                        try {
                            await onPdfUpload?.(file);
                            console.log("Selected file:", file.name);
                        }
                        catch (error) {
                            console.error("Error uploading file:", error);
                        }
                        finally {
                            setIsUploadingPdf(false);
                        }
                    }
                } }), renderFormFields(), errors['table'] && (_jsx(Box, { sx: { mt: 2, mb: 2 }, children: _jsx(Typography, { variant: "caption", color: "error", sx: {
                        fontFamily: "IBM Plex Sans, sans-serif",
                        fontSize: "14px",
                        display: "block",
                        backgroundColor: "#ffebee",
                        padding: "8px 12px",
                        borderRadius: "4px",
                        border: "1px solid #e57373"
                    }, children: errors['table'] }) })), _jsxs(Box, { sx: {
                    mt: 3,
                    display: "flex",
                    gap: 2,
                    justifyContent: "flex-end",
                }, children: [form?.cancelButtonText && !isSubmitted && (_jsx(Button, { variant: "outlined", onClick: onCancel, sx: {
                            color: "#0E497A",
                            borderColor: "#0E497A",
                            backgroundColor: "#fff",
                            fontFamily: "IBM Plex Sans, sans-serif",
                            borderRadius: "25px",
                            padding: "8px 24px",
                            textTransform: "none",
                            fontWeight: 500,
                            marginRight: "auto",
                            "&:hover": {
                                borderColor: "#0E497A",
                                backgroundColor: "rgba(14, 73, 122, 0.04)",
                            },
                        }, children: form.cancelButtonText })), !isSubmitted ? (_jsx(Button, { variant: "outlined", disabled: isSubmitting || isSubmitted || !isFormValid, onClick: handleSubmit, sx: {
                            color: "#0E497A",
                            borderColor: "#0E497A",
                            backgroundColor: "#fff",
                            fontFamily: "IBM Plex Sans, sans-serif",
                            borderRadius: "25px",
                            padding: "8px 24px",
                            textTransform: "none",
                            fontWeight: 500,
                            "&:hover": {
                                borderColor: "#0E497A",
                                backgroundColor: "rgba(14, 73, 122, 0.04)",
                            },
                            "&.Mui-disabled": {
                                color: "#9e9e9e",
                                borderColor: "#e0e0e0",
                                backgroundColor: "#f5f5f5",
                            },
                        }, children: finalSubmitButtonText })) : (_jsx(_Fragment, { children: enableReviewMode && (_jsxs(_Fragment, { children: [_jsx(Button, { variant: "outlined", disabled: isConfirmed || isSubmitting, onClick: handleEdit, sx: {
                                        color: "#0E497A",
                                        borderColor: "#0E497A",
                                        backgroundColor: "#fff",
                                        fontFamily: "IBM Plex Sans, sans-serif",
                                        borderRadius: "25px",
                                        padding: "8px 24px",
                                        textTransform: "none",
                                        fontWeight: 500,
                                        "&:hover": {
                                            borderColor: "#0E497A",
                                            backgroundColor: "rgba(14, 73, 122, 0.04)",
                                        },
                                        "&.Mui-disabled": {
                                            color: "#9e9e9e",
                                            borderColor: "#e0e0e0",
                                            backgroundColor: "#f5f5f5",
                                        },
                                    }, children: editButtonText }), _jsx(Button, { variant: "contained", disabled: isConfirmed || isSubmitting, onClick: handleConfirm, sx: {
                                        color: "#fff",
                                        backgroundColor: "#1BBC9B",
                                        fontFamily: "IBM Plex Sans, sans-serif",
                                        borderRadius: "25px",
                                        padding: "8px 24px",
                                        textTransform: "none",
                                        fontWeight: 500,
                                        boxShadow: "none",
                                        "&:hover": {
                                            backgroundColor: "#17a085",
                                            boxShadow: "none",
                                        },
                                        "&.Mui-disabled": {
                                            color: "#fff",
                                            backgroundColor: "#b0b0b0",
                                        },
                                    }, children: confirmButtonText })] })) }))] })] })) : null;
};
export default Form;
